import { useEffect, useState } from 'react';

const REFERENCE_WIDTH = 1920;
const REFERENCE_HEIGHT = 1080;
const MIN_SCALE = 0.7; // Minimum scale to ensure readability on smaller screens
const DEBOUNCE_MS = 100; // Debounce resize events for performance

/**
 * Compute the global scale factor based on the current viewport.
 * Reference resolution: 1920 × 1080
 * Scale factor = Math.min(innerWidth / 1920, innerHeight / 1080)
 */
function computeScale(): number {
  if (typeof window === 'undefined') return 1;
  return Math.max(
    MIN_SCALE,
    Math.min(window.innerWidth / REFERENCE_WIDTH, window.innerHeight / REFERENCE_HEIGHT)
  );
}

/**
 * Hook to calculate and provide a global scale factor for resolution-independent UI.
 *
 * - Calculates the scale factor from the viewport dimensions
 * - Updates the `--app-scale` CSS custom property on `:root`
 * - Debounced resize listener for performance
 * - SSR-safe (returns 1 on the server)
 *
 * Reference resolution: 1920 × 1080
 */
export function useScale() {
  const [scale, setScale] = useState(computeScale);

  useEffect(() => {
    const updateScale = () => {
      const newScale = computeScale();
      setScale((prev) => (prev !== newScale ? newScale : prev));
      document.documentElement.style.setProperty('--app-scale', newScale.toString());
    };

    // Initial update to ensure the CSS variable is set synchronously
    updateScale();

    // Debounced resize listener
    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      if (resizeTimer !== undefined) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateScale, DEBOUNCE_MS);
    };

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });

    return () => {
      if (resizeTimer !== undefined) clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  return scale;
}
